import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import { Badge, Box, Tooltip } from '@mui/material';

import { ComponentDesign } from '@paragon/contract-library';

import {
  getColorForComponentWithWorkingChanges,
  getPassOrFailColor,
  getPassOrFailColorFromAnalysisSet,
} from '../../../../../capacityColors';
import { RootState } from '../../../../../store';
import { createHasDelayedNotifications } from '../../../../../store/selectors/delayedNotificationSelectors';
import { getActiveAnalysisSet } from '../../../../../store/selectors/getActiveAnalysisSetSelector';
import { getLinkedComponentDesignGuids } from '../../../../../store/selectors/layoutDataSelectors';
import { createDoWorkingChangesExistOnComponentDesign } from '../../../../../store/selectors/workingChangesSelectors';
import { useAppSelector } from '../../../../hooks';
import { DisplayNameProviderProps } from './PrimaryRowText';

function createGetCapacityColorForComponent(componentDesignGuid: string) {
  const doWorkingChangesExistOnComponentDesign = createDoWorkingChangesExistOnComponentDesign(componentDesignGuid);

  return (state: RootState) => {
    if (
      state.application.insertingObjectInfo?.type === 'componentDesign' &&
      state.application.insertingObjectInfo.loadedInfo.isLoaded &&
      state.application.insertingObjectInfo.loadedInfo.guid === componentDesignGuid
    ) {
      const { memberAnalysisInfo } = state.application.insertingObjectInfo;
      if (memberAnalysisInfo) {
        return getPassOrFailColor({ passes: memberAnalysisInfo.capacityRatio <= 1 });
      }
    }

    if (
      state.application.rewebbingInfo?.memberAnalysisInfo &&
      state.selections.activeComponentDesignGuid === componentDesignGuid
    ) {
      return getPassOrFailColor({ passes: state.application.rewebbingInfo.memberAnalysisInfo.capacityRatio <= 1 });
    }

    const activeAnalysisSet = getActiveAnalysisSet(state);
    const activeAnalysisSetExistsAndIsForThisComponentDesign =
      activeAnalysisSet && activeAnalysisSet.componentDesign.guid === componentDesignGuid;
    if (activeAnalysisSetExistsAndIsForThisComponentDesign) {
      return getPassOrFailColorFromAnalysisSet(activeAnalysisSet);
    }

    const hasWorkingChanges = doWorkingChangesExistOnComponentDesign(state);
    if (hasWorkingChanges) {
      return getColorForComponentWithWorkingChanges();
    }

    const timestamps = state.componentDesignTimestamps[componentDesignGuid];
    if (timestamps && timestamps.lastAnalyzedCapacity) {
      return getPassOrFailColor({ passes: timestamps.lastAnalyzedCapacity <= 1 && !timestamps.lastAnalysisHadErrors });
    }

    return 'white';
  };
}

export function ColoredComponentName(props: { componentDesignGuid: string; children: ReactNode }) {
  const isInNonCapacityContext = useAppSelector(
    (state) =>
      state.application.openContext?.type === 'shared-component-set' ||
      state.application.openContext?.type === 'station',
  );

  const getCapacityColorForComponent = useMemo(
    () => createGetCapacityColorForComponent(props.componentDesignGuid),
    [props.componentDesignGuid],
  );

  const capacityColor = useAppSelector(getCapacityColorForComponent);
  const color = isInNonCapacityContext ? '#FFF' : capacityColor;

  return (
    <Box component="span" sx={{ color }}>
      {props.children}
    </Box>
  );
}

const BADGE_ANCHOR = { vertical: 'top', horizontal: 'right' } as const;

export const ComponentNameFormatter: FunctionComponent<DisplayNameProviderProps<ComponentDesign>> = (props) => {
  const hasDelayedNotifications = useAppSelector(createHasDelayedNotifications);
  const linkedToTrussEnvelopes = useAppSelector((state) =>
    getLinkedComponentDesignGuids(state).has(props.row.item.guid),
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ColoredComponentName componentDesignGuid={props.row.item.guid}>
        <Badge
          color="secondary"
          variant="dot"
          anchorOrigin={BADGE_ANCHOR}
          invisible={!hasDelayedNotifications(props.row.item.guid)}
        >
          {props.displayName}
        </Badge>
      </ColoredComponentName>

      {linkedToTrussEnvelopes && (
        <Tooltip title="This component is linked to one or more truss envelopes.">
          <LinkIcon sx={{ marginLeft: 1 }} />
        </Tooltip>
      )}
    </Box>
  );
};
